import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { AVDELNING_FARGER } from '../../lib/constants'

// Gemensam logik för att bevilja/neka en ansökan (används både i inkorgen och från schemat)
export async function svaraPaAnsokan({ ansokan, status, kommentar, admin }) {
    const adminNamn = `${admin.fornamn} ${admin.efternamn}`
    await supabase.from('ansokningar').update({
      status,
      admin_kommentar: kommentar,
      besvarad_av: admin.id,
      besvarad_av_namn: adminNamn,
      besvarad_at: new Date().toISOString()
    }).eq('id', ansokan.id)

    // OBS: semester ("ledighet") skrivs INTE längre in automatiskt på schemat vid
    // godkännande – admin sätter det manuellt i helhetsschemat. Föräldraledighet
    // och ATF fortsätter appliceras direkt som förut.
    // Gulmarkerad ledighet: bevilja -> grön ✦ på dagarna, neka -> passen återställs.
    // Ingen notis per pass skickas; medarbetaren får bara notisen om ansökans svar.
    if (ansokan.typ === 'ledighet') {
      try {
        if (status === 'beviljad') await supabase.rpc('bevilja_gulmarkering', { p_ansokan_id: ansokan.id })
        if (status === 'nekad') await supabase.rpc('aterstall_gulmarkering', { p_ansokan_id: ansokan.id })
      } catch (e) {
        console.error('Gulmarkering:', e)
      }
    }
    if (status === 'beviljad' && ansokan.typ === 'foraldraledighet') {
      await beviljaForaldraledighet(ansokan, admin)
    }
    if (status === 'beviljad' && ansokan.typ === 'atf') {
      await beviljaAtf(ansokan, admin)
    }

    // Passbyte: kollegan som fick förfrågan ska också få besked om ansökan beviljas/nekas
    if (ansokan.typ === 'passbyte' && (status === 'beviljad' || status === 'nekad')) {
      try {
        const { data: notisRader } = await supabase
          .from('ansokningar')
          .select('mottagare_id')
          .eq('typ', 'passandring_notis')
          .eq('passandring_fran', 'Passbytesbegäran')
          .eq('skapad_av', ansokan.skapad_av)
          .eq('fran_datum', ansokan.fran_datum)
          .order('skapad_at', { ascending: false })
          .limit(1)
        const kollegaId = notisRader?.[0]?.mottagare_id
        if (kollegaId) {
          const beviljad = status === 'beviljad'
          await supabase.from('ansokningar').insert({
            typ: 'passandring_notis',
            skapad_av: admin.id,
            skapad_av_namn: adminNamn,
            avdelning: ansokan.avdelning,
            fran_datum: ansokan.fran_datum,
            till_datum: ansokan.till_datum,
            mottagare_id: kollegaId,
            status: beviljad ? 'beviljad' : 'nekad',
            admin_kommentar: `Passbytet med ${ansokan.skapad_av_namn} har ${beviljad ? 'godkänts' : 'nekats'}.${kommentar ? ` ${kommentar}` : ''}`,
            passandring_fran: beviljad ? 'Passbyte beviljat' : 'Passbyte nekat',
            passandring_till: ansokan.skapad_av_namn,
            passandring_datum: ansokan.fran_datum,
            arkiverad_medarbetare: false,
            sedd: false
          })
        }
      } catch (e) {
        console.error('Notis till kollega vid passbyte:', e)
      }
    }
}

async function beviljaForaldraledighet(ansökan, admin) {
  const start = new Date(ansökan.fran_datum)
  const slut = new Date(ansökan.till_datum)
  let d = new Date(start)
  while (d <= slut) {
    const datumStr = d.toISOString().split('T')[0]
    const { data: passRader } = await supabase
      .from('pass')
      .select('id, passtyp_namn')
      .eq('medarbetare_id', ansökan.skapad_av)
      .eq('datum', datumStr)
    for (const p of passRader || []) {
      const namn = p.passtyp_namn || ''
      const nyttNamn = namn.toUpperCase().startsWith('L') ? namn : 'L' + namn
      await supabase.from('pass')
        .update({ kategori: 'beviljad_foraldraledighet', passtyp_namn: nyttNamn })
        .eq('id', p.id)
    }
    d.setDate(d.getDate() + 1)
  }
}

async function beviljaAtf(ansökan, admin) {
  const { data: pass } = await supabase.from('pass')
    .update({
      har_atf: true,
      atf_fran: ansökan.atf_fran_tid,
      atf_till: ansökan.atf_till_tid
    })
    .eq('medarbetare_id', ansökan.skapad_av)
    .eq('datum', ansökan.fran_datum)
    .select()
    .single()

  if (pass) {
    const adminNamn = `${admin.fornamn} ${admin.efternamn}`
    const franTxt = (ansökan.atf_fran_tid || '').substring(0,5).replace(':', '.')
    const tillTxt = (ansökan.atf_till_tid || '').substring(0,5).replace(':', '.')
    await supabase.from('pass_historik').insert({
      pass_id: pass.id,
      andrad_av: admin.id,
      andrad_av_namn: adminNamn,
      gammal_passtyp: pass.passtyp_namn || '',
      ny_passtyp: pass.passtyp_namn || '',
      gammal_kategori: pass.kategori || 'ordinarie',
      ny_kategori: pass.kategori || 'ordinarie',
      notering: `ATF mellan ${franTxt}-${tillTxt}`,
    })
  }
}

export function InkorgPage() {
  const { medarbetare: admin } = useAuth()
  const navigate = useNavigate()
  const [ansokningar, setAnsokningar] = useState([])
  const [vald, setVald] = useState(null)
  const [kommentar, setKommentar] = useState('')
  const [loading, setLoading] = useState(true)
  const [filterAvd, setFilterAvd] = useState('Alla')
  const [aktiv, setAktiv] = useState('inkorg') // 'inkorg' | 'arkiv'
  const [markerade, setMarkerade] = useState([])
  // Gulmarkering av ledighetsansökan (semester som ännu inte är beviljad)
  const [gulAntal, setGulAntal] = useState(null)
  const [gulBusy, setGulBusy] = useState(false)
  const [gulMeddelande, setGulMeddelande] = useState('')

  async function laddaGulAntal(ansokanId) {
    const { count } = await supabase
      .from('pass')
      .select('id', { count: 'exact', head: true })
      .eq('gul_ansokan_id', ansokanId)
    setGulAntal(count || 0)
  }

  useEffect(() => {
    setGulMeddelande('')
    if (vald && vald.typ === 'ledighet' && vald.status === 'väntar') {
      setGulAntal(null)
      laddaGulAntal(vald.id)
    } else {
      setGulAntal(null)
    }
  }, [vald?.id])

  async function gulmarkera() {
    setGulBusy(true)
    setGulMeddelande('')
    const { data, error } = await supabase.rpc('gulmarkera_ledighet', { p_ansokan_id: vald.id })
    if (error) {
      setGulMeddelande('Kunde inte gulmarkera: ' + error.message)
    } else {
      setGulMeddelande(`${data} dagar gulmarkerades i schemat.`)
    }
    await laddaGulAntal(vald.id)
    setGulBusy(false)
  }

  async function taBortGul() {
    setGulBusy(true)
    setGulMeddelande('')
    const { error } = await supabase.rpc('aterstall_gulmarkering', { p_ansokan_id: vald.id })
    if (error) setGulMeddelande('Kunde inte ta bort gulmarkeringen: ' + error.message)
    else setGulMeddelande('Gulmarkeringen är borttagen och passen är återställda.')
    await laddaGulAntal(vald.id)
    setGulBusy(false)
  }

  useEffect(() => {
    hamtaAnsokningar()
    const interval = setInterval(hamtaAnsokningar, 2000)
    return () => clearInterval(interval)
  }, [])

  async function hamtaAnsokningar() {
    // Rensa gamla arkiverade (>180 dagar) - bara om BÅDA admin och medarbetare arkiverat
    const grans = new Date()
    grans.setDate(grans.getDate() - 180)
    await supabase.from('ansokningar').delete()
      .eq('arkiverad_admin', true)
      .eq('arkiverad_medarbetare', true)
      .lt('arkiverad_admin_at', grans.toISOString())

    const { data } = await supabase
      .from('ansokningar')
      .select('*')
      .neq('typ', 'passandring_notis')
      .order('skapad_at', { ascending: false })
    setAnsokningar(data || [])
    setLoading(false)
  }

  async function svara(status) {
    if (!vald) return
    await svaraPaAnsokan({ ansokan: vald, status, kommentar, admin })
    setVald(null)
    setKommentar('')
    hamtaAnsokningar()
  }

  async function angra(a) {
    // Ångrar man ett beviljande av gulmarkerad ledighet blir dagarna gula (ej beviljade) igen
    if (a.typ === 'ledighet' && a.status === 'beviljad') {
      try { await supabase.rpc('gul_tillbaka_till_vantar', { p_ansokan_id: a.id }) } catch (e) { console.error(e) }
    }
    // Passbyte: ta bort beskedet (beviljat/nekat) som kollegan fick, annars står det kvar hos kollegan
    if (a.typ === 'passbyte') {
      try {
        const { error: delErr } = await supabase.from('ansokningar').delete()
          .eq('typ', 'passandring_notis')
          .in('passandring_fran', ['Passbyte beviljat', 'Passbyte nekat'])
          .eq('passandring_till', a.skapad_av_namn)
          .eq('fran_datum', a.fran_datum)
        if (delErr) console.error('Kunde inte ta bort kollegans besked:', delErr)
      } catch (e) { console.error(e) }
    }
    // Uppdatera i två steg för att undvika RLS-problem
    const { error: e1 } = await supabase.from('ansokningar').update({
      status: 'väntar',
      admin_kommentar: null,
      besvarad_av: null,
      besvarad_av_namn: null,
      besvarad_at: null,
    }).eq('id', a.id)

    const { error: e2 } = await supabase.from('ansokningar').update({
      arkiverad_medarbetare: false,
      arkiverad_medarbetare_at: null,
      sedd: false
    }).eq('id', a.id)

    if (e1 || e2) {
      console.error('Ångra fel:', e1, e2)
      alert('Fel vid ångra: ' + (e1?.message || e2?.message || 'Okänt fel'))
    }

    setVald(null)
    hamtaAnsokningar()
  }

  async function flyttaTillbaka(ids) {
    await Promise.all(ids.map(id => supabase.from('ansokningar').update({
      arkiverad_admin: false,
      arkiverad_admin_at: null
    }).eq('id', id)))
    setMarkerade([])
    hamtaAnsokningar()
  }



  async function arkiveraMarkerade() {
    await Promise.all(markerade.map(id =>
      supabase.from('ansokningar').update({
        arkiverad_admin: true,
        arkiverad_admin_at: new Date().toISOString()
      }).eq('id', id)
    ))
    setMarkerade([])
    hamtaAnsokningar()
  }

  function toggleMarkerad(id, e) {
    e.stopPropagation()
    setMarkerade(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function toggleAllaMarkerade() {
    const allaIds = visaLista.map(a => a.id)
    setMarkerade(prev => prev.length === allaIds.length ? [] : allaIds)
  }

  async function arkivera(a) {
    await supabase.from('ansokningar').update({
      arkiverad_admin: true,
      arkiverad_admin_at: new Date().toISOString()
    }).eq('id', a.id)
    setVald(null)
    hamtaAnsokningar()
  }

  async function avarkivera(a) {
    await supabase.from('ansokningar').update({
      arkiverad_admin: false,
      arkiverad_admin_at: null
    }).eq('id', a.id)
    hamtaAnsokningar()
  }

  async function beviljaLedighet(ansökan) {
    const start = new Date(ansökan.fran_datum)
    const slut = new Date(ansökan.till_datum)
    let d = new Date(start)
    while (d <= slut) {
      const datumStr = d.toISOString().split('T')[0]
      const { data: passRader } = await supabase
        .from('pass')
        .select('id, passtyp_namn')
        .eq('medarbetare_id', ansökan.skapad_av)
        .eq('datum', datumStr)
      for (const p of passRader || []) {
        const namn = p.passtyp_namn || ''
        const nyttNamn = namn.toUpperCase().startsWith('S') ? namn : 'S' + namn
        await supabase.from('pass')
          .update({ kategori: 'beviljad_semester', passtyp_namn: nyttNamn })
          .eq('id', p.id)
      }
      d.setDate(d.getDate() + 1)
    }
  }

  const TYPER = { ledighet: 'Ledighetsansökan', foraldraledighet: 'Föräldraledighet', passbyte: 'Passbyte', boka_dag: 'Boka dag', atf: 'ATF', flytta_pass: 'Flytta D-dag' }
  const TAG_CLS = { ledighet: 'tag-blue', foraldraledighet: 'tag-green', passbyte: 'tag-purple', boka_dag: 'tag-indigo', atf: 'tag-green', flytta_pass: 'tag-purple' }
  const STATUS_CLS = { väntar: 'tag-yellow', beviljad: 'tag-green', nekad: 'tag-red' }
  const STATUS_LBL = { väntar: 'Väntar', beviljad: 'Beviljad', nekad: 'Nekad' }

  const inkorg = ansokningar.filter(a => !a.arkiverad_admin)
  const arkiv = ansokningar.filter(a => a.arkiverad_admin)

  const filtreraAvd = (lista) => filterAvd === 'Alla' ? lista : lista.filter(a => a.avdelning === filterAvd)

  const visaLista = filtreraAvd(aktiv === 'inkorg' ? inkorg : arkiv)
  // Rensa markerade när listan ändras

  const obehandlade = inkorg.filter(a => a.status === 'väntar').length

  if (loading) return <div className="spinner" />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      <div className="content-header">
        <div>
          <h1>Inkorg</h1>
          <div className="content-sub">
            {obehandlade > 0 ? `${obehandlade} obehandlade ansökningar` : 'Alla ansökningar behandlade'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {markerade.length > 0 && aktiv === 'inkorg' && (
            <button className="btn btn-secondary btn-sm" onClick={arkiveraMarkerade} style={{ fontSize: '.78rem' }}>
              🗄 Arkivera {markerade.length} markerade
            </button>
          )}
          {markerade.length > 0 && aktiv === 'arkiv' && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => { flyttaTillbaka(markerade) }} style={{ fontSize: '.78rem' }}>
                ↩ Flytta till inkorg
              </button>

            </>
          )}
          <select
            className="form-control"
            style={{ margin: 0, width: 160 }}
            value={filterAvd}
            onChange={e => setFilterAvd(e.target.value)}
          >
            <option>Alla</option>
            {['RN', 'LN MMÖ', 'LN NKP', 'DISP'].map(a => <option key={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Flikar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #f0f0f0', paddingLeft: 26, flexShrink: 0 }}>
        {[
          { id: 'inkorg', label: `📥 Inkorg ${obehandlade > 0 ? `(${obehandlade})` : ''}` },
          { id: 'arkiv', label: `🗄 Arkiv (${arkiv.length})` }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setAktiv(f.id)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: aktiv === f.id ? 700 : 400,
              color: aktiv === f.id ? '#F11C0A' : '#888',
              borderBottom: aktiv === f.id ? '2px solid #F11C0A' : '2px solid transparent',
              marginBottom: -2, fontSize: '.85rem', fontFamily: 'inherit'
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="content-body" style={{ overflowY: 'auto' }}>
        {visaLista.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 10px', fontSize: '.8rem', color: '#888' }}>
            <input
              type="checkbox"
              checked={markerade.length === visaLista.length && visaLista.length > 0}
              onChange={toggleAllaMarkerade}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            <span>Markera alla</span>
          </div>
        )}
        {visaLista.length === 0 ? (
          <div className="card">
            <p style={{ color: '#aaa', textAlign: 'center' }}>
              {aktiv === 'arkiv' ? 'Inga arkiverade ansökningar' : 'Inga ansökningar att visa'}
            </p>
          </div>
        ) : (
          visaLista.map(a => (
            <div
              key={a.id}
              style={{
                background: markerade.includes(a.id) ? '#f0f7ff' : '#fff',
                border: `1px solid ${markerade.includes(a.id) ? '#90caf9' : '#ececec'}`,
                borderLeft: `4px solid ${a.status === 'beviljad' ? '#28a745' : a.status === 'nekad' ? '#dc3545' : '#F11C0A'}`,
                borderRadius: 7, padding: '13px 16px', marginBottom: 9,
                cursor: 'pointer', transition: 'box-shadow .15s',
                opacity: a.status !== 'väntar' ? .75 : 1,
                display: 'flex', gap: 10, alignItems: 'flex-start'
              }}
              onClick={() => { setVald(a); setKommentar(a.admin_kommentar || '') }}
            >
              <input
                  type="checkbox"
                  checked={markerade.includes(a.id)}
                  onChange={e => toggleMarkerad(a.id, e)}
                  onClick={e => e.stopPropagation()}
                  style={{ width: 15, height: 15, cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
                />
              <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 5 }}>
                <span className={`tag ${TAG_CLS[a.typ]}`}>{TYPER[a.typ]}</span>
                <span className={`tag ${STATUS_CLS[a.status]}`}>{STATUS_LBL[a.status]}</span>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: AVDELNING_FARGER[a.avdelning] || '#999', display: 'inline-block' }} />
                <span style={{ fontSize: '.71rem', color: '#aaa' }}>{a.avdelning}</span>
                {a.arkiverad && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginLeft: 'auto', fontSize: '.7rem', padding: '2px 8px' }}
                    onClick={e => { e.stopPropagation(); avarkivera(a) }}
                  >
                    ↩ Återställ
                  </button>
                )}
              </div>
              <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{a.skapad_av_namn}</div>
              <div style={{ fontSize: '.8rem', color: '#666', marginTop: 3 }}>
                {(a.typ === 'ledighet' || a.typ === 'foraldraledighet') && `${a.fran_datum} – ${a.till_datum}`}
                {a.typ === 'boka_dag' && a.fran_datum}
                {a.typ === 'flytta_pass' && `${a.fran_datum} → ${a.till_datum}`}
                {a.typ === 'atf' && `${a.fran_datum} · ${a.atf_fran_tid?.substring(0,5)}–${a.atf_till_tid?.substring(0,5)}`}
                {a.medarbetare_kommentar && ` · "${a.medarbetare_kommentar}"`}
              </div>
              <div style={{ fontSize: '.72rem', color: '#bbb', marginTop: 5 }}>
                {new Date(a.skapad_at).toLocaleString('sv-SE')}
                {a.besvarad_av_namn && ` · Besvarad av ${a.besvarad_av_namn}`}
                {a.arkiverad_admin_at && ` · Arkiverad ${new Date(a.arkiverad_admin_at).toLocaleDateString('sv-SE')}`}
              </div>
              </div>
            </div>
          ))
        )}

        {aktiv === 'arkiv' && arkiv.length > 0 && (
          <div style={{ textAlign: 'center', fontSize: '.75rem', color: '#bbb', padding: '10px 0' }}>
            Arkiverade ärenden raderas automatiskt efter 6 månader (180 dagar)
          </div>
        )}
      </div>

      {/* SVARA-MODAL */}
      {vald && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setVald(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h3>📩 {TYPER[vald.typ]}</h3>
              <button className="modal-close" onClick={() => setVald(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="info-box info-box-red" style={{ marginBottom: 13 }}>
                <div style={{ fontWeight: 700, marginBottom: 3 }}>{vald.skapad_av_namn} · {vald.avdelning}</div>
                <div style={{ fontSize: '.8rem' }}>
                  {(vald.typ === 'ledighet' || vald.typ === 'foraldraledighet') && `${vald.fran_datum} – ${vald.till_datum}`}
                  {vald.typ === 'boka_dag' && vald.fran_datum}
                  {vald.typ === 'flytta_pass' && `${vald.fran_datum} → ${vald.till_datum}`}
                  {vald.typ === 'atf' && `${vald.fran_datum} · ${vald.atf_fran_tid?.substring(0,5)}–${vald.atf_till_tid?.substring(0,5)}`}
                  {vald.medarbetare_kommentar && <div style={{ marginTop: 4 }}>"{vald.medarbetare_kommentar}"</div>}
                </div>
              </div>
              {vald.typ === 'ledighet' && vald.status === 'väntar' && (
                <div className="info-box info-box-yellow" style={{ marginBottom: 13 }}>
                  {gulAntal === null ? 'Kollar schemat...' : gulAntal > 0 ? (
                    <>
                      <strong>{gulAntal} dagar är gulmarkerade i schemat</strong> (ej beviljade). När du klickar Bevilja blir de beviljad semester med grön ✦, och medarbetaren får bara en notis.
                    </>
                  ) : (
                    <>
                      Perioden är inte gulmarkerad i schemat. Klickar du Bevilja skrivs inget in automatiskt. Gulmarkera först om du vill att dagarna ska bli beviljad semester med grön ✦ när du beviljar.
                    </>
                  )}
                  {gulAntal !== null && (
                    <div style={{ marginTop: 8 }}>
                      {gulAntal > 0 ? (
                        <button className="btn btn-secondary btn-sm" onClick={taBortGul} disabled={gulBusy}>Ta bort gulmarkering</button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={gulmarkera} disabled={gulBusy}>Gulmarkera i schemat</button>
                      )}
                    </div>
                  )}
                  {gulMeddelande && <div style={{ marginTop: 6, fontSize: '.78rem' }}>{gulMeddelande}</div>}
                </div>
              )}
              {vald.typ === 'flytta_pass' && vald.status === 'väntar' && (
                <div className="info-box info-box-yellow" style={{ marginBottom: 13 }}>
                  Kom ihåg: godkänner du den här ansökan flyttas D-dagen <strong>inte</strong> automatiskt. Flytta passet manuellt i helhetsschemat och markera det som passbyte (rosa). Flytten sker på medarbetarens egen begäran.
                </div>
              )}
              <label className="form-label">Kommentar till medarbetaren</label>
              <textarea
                className="form-control" rows={3}
                placeholder="Valfri kommentar skickas till medarbetaren..."
                value={kommentar}
                onChange={e => setKommentar(e.target.value)}
              />
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {aktiv === 'inkorg' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => arkivera(vald)} style={{ fontSize: '.78rem' }}>🗄 Arkivera</button>
                )}
                {aktiv === 'arkiv' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { flyttaTillbaka([vald.id]); setVald(null) }} style={{ fontSize: '.78rem' }}>↩ Till inkorg</button>
                )}
                <button className="btn btn-secondary btn-sm" style={{ fontSize: '.78rem' }}
                  onClick={() => navigate('/admin/schema', { state: { ansokan: {
                    id: vald.id, typ: vald.typ, namn: vald.skapad_av_namn, avdelning: vald.avdelning,
                    fran: vald.fran_datum, till: vald.till_datum, kommentar: vald.medarbetare_kommentar,
                    atfFran: vald.atf_fran_tid, atfTill: vald.atf_till_tid,
                    rad: vald, kommentar_admin: kommentar
                  } } })}>
                  Öppna i schemat
                </button>

              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setVald(null)}>Avbryt</button>
                {vald.status === 'väntar' && (
                  <>
                    <button className="btn btn-danger" onClick={() => svara('nekad')}>✕ Neka</button>
                    <button className="btn btn-success" onClick={() => svara('beviljad')}>✓ Bevilja</button>
                  </>
                )}
                {vald.status !== 'väntar' && (
                  <button className="btn btn-secondary" onClick={() => angra(vald)}>↩ Ångra</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
